import React, { useMemo } from 'react';
import CardContainer from '../../card/card-container.jsx';
import UsoSirioKpis from './uso-sirio-kpis.jsx';
import UsoSirioBarras from './uso-sirio-barras.jsx';
import UsoSirioTabla from './uso-sirio-tabla.jsx';
import EmptyStateChart from '../empty-state-chart.js';
import { Colors, ChartColors } from '../../../constants/Colors.js';

const UsoSirioUsuarios = ({ datos }) => {
  const roles = datos.roles || [];
  const resumen = datos.resumen || {};
  const porCooperativa = datos.porCooperativa || [];
  const versiones = datos.versiones || [];
  const pendientes = datos.pendientesSync || [];

  const series = roles.map(rol => ({ key: rol, label: rol }));

  const columnas = useMemo(
    () => [
      { key: 'cooperativa', label: 'Cooperativa', tipo: 'texto' },
      ...roles.map(rol => ({ key: rol, label: rol, tipo: 'numero' })),
      { key: 'total', label: 'Total', tipo: 'numero' },
      { key: 'activos', label: 'Activos', tipo: 'numero' },
      { key: 'inactivos', label: 'Inactivos', tipo: 'numero' },
      { key: 'nunca', label: 'Nunca', tipo: 'numero' },
      { key: 'pendientes_sync', label: 'Sin sync', tipo: 'numero' },
    ],
    [roles]
  );

  const maxVersion = versiones.reduce((acc, version) => Math.max(acc, version.n), 0);

  return (
    <>
      <UsoSirioKpis
        items={[
          {
            label: 'Usuarios activos en Sirio',
            value: (resumen.total ?? 0).toLocaleString('es-MX'),
            hint: `en ${resumen.cooperativas ?? 0} cooperativas`,
          },
          {
            label: 'Entraron recientemente',
            value: resumen.activos ?? 0,
            hint: `sesión en los últimos ${resumen.diasInactividad ?? 90} días`,
            color: ChartColors.estado.alto,
          },
          { label: 'Sin entrar nunca', value: resumen.nunca ?? 0, color: ChartColors.estado.nulo },
          {
            label: 'En la última versión',
            value: resumen.conVersionApp ? `${Math.round(((resumen.enUltimaVersion || 0) / resumen.conVersionApp) * 100)}%` : '—',
            hint: resumen.ultimaVersion ? `v${resumen.ultimaVersion} · ${resumen.conVersionApp} con app` : 'sin datos de app',
          },
          { label: 'Con datos sin sincronizar', value: resumen.pendientesSync ?? 0, color: ChartColors.estado.bajo },
        ]}
      />

      <CardContainer style={{ marginBottom: 16 }}>
        <h6 className="font-lato-normal-500 heading-box mb-3">Usuarios por cooperativa y rol</h6>
        <UsoSirioBarras series={series} filas={porCooperativa} etiquetaTotal="usuarios" />
      </CardContainer>

      <div className="row">
        <div className="col-12 col-lg-6 mb-3">
          <CardContainer height="100%">
            <h6 className="font-lato-normal-500 heading-box mb-1">Versión de la app en uso</h6>
            <small className="d-block mb-3" style={{ color: Colors.neutral_400 }}>
              Última versión reportada por cada usuario al sincronizar
            </small>
            {versiones.length === 0 ? (
              <EmptyStateChart message="Ningún usuario ha reportado versión de app" />
            ) : (
              versiones
                .slice()
                .reverse()
                .map(version => (
                  <div key={version.version} className="d-flex align-items-center mb-2" style={{ gap: 10 }}>
                    <span style={{ fontSize: 12, minWidth: 52, color: Colors.neutral_800 }}>v{version.version}</span>
                    <span style={{ flex: 1, backgroundColor: Colors.neutral_50, borderRadius: 4, height: 14 }}>
                      <span
                        style={{
                          display: 'block',
                          width: `${maxVersion ? (version.n / maxVersion) * 100 : 0}%`,
                          backgroundColor:
                            version.version === resumen.ultimaVersion ? ChartColors.sequential[3] : ChartColors.sequential[0],
                          borderRadius: 4,
                          height: 14,
                        }}
                      />
                    </span>
                    <span style={{ fontSize: 12, minWidth: 34, textAlign: 'right', color: Colors.gray_base }}>{version.n}</span>
                  </div>
                ))
            )}
          </CardContainer>
        </div>

        <div className="col-12 col-lg-6 mb-3">
          <CardContainer height="100%">
            <h6 className="font-lato-normal-500 heading-box mb-1">Datos sin sincronizar</h6>
            <small className="d-block mb-3" style={{ color: Colors.neutral_400 }}>
              {pendientes.length === 0
                ? 'Ningún usuario reporta información pendiente de subir'
                : `${pendientes.length} usuarios con información capturada en la app que no ha llegado a Sirio`}
            </small>
            <div style={{ maxHeight: 260, overflowY: 'auto' }}>
              {pendientes.map((usuario, i) => (
                <div key={i} className="pb-2 mb-2" style={{ borderBottom: `1px solid ${Colors.neutral_50}` }}>
                  <div style={{ fontSize: 13, color: Colors.neutral_800 }}>
                    {usuario.nombre} <span style={{ color: Colors.neutral_400 }}>· {usuario.rol}</span>
                  </div>
                  <div style={{ fontSize: 11, color: Colors.gray_base }}>{usuario.cooperativa}</div>
                  <div style={{ fontSize: 11, color: ChartColors.estado.bajo }}>{usuario.pendiente}</div>
                </div>
              ))}
            </div>
          </CardContainer>
        </div>
      </div>

      <CardContainer>
        <h6 className="font-lato-normal-500 heading-box mb-3">Desglose completo</h6>
        <UsoSirioTabla columnas={columnas} filas={porCooperativa} hoja="Usuarios" nombreArchivo="uso_sirio_usuarios" />
      </CardContainer>
    </>
  );
};

export default UsoSirioUsuarios;
